// src/pages/WhitepaperPage.jsx

import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import '../styles/whitepaper-page.css';

const WhitepaperPage = () => {
  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="whitepaper-page">
      <section className="hero-section">
        <div className="container">
          <h1 className="gradient-title">The Aledger Economic Model</h1>
          <p className="hero-subtitle">
            A revolutionary tokenomic framework that creates mathematical value growth through hardware-enforced staking
          </p>
        </div>
      </section>

      <section className="whitepaper-intro">
        <div className="container">
          <div className="glass-card">
            <h2>Executive Summary</h2>
            <p>
              The Aledger ecosystem introduces a revolutionary economic model that transforms physical WiFi devices 
              into wealth-generating mining nodes through an innovative tokenomic design. This whitepaper outlines 
              the multi-layered economic framework that drives value creation, retention, and sustainable growth 
              throughout the ecosystem.
            </p>
            
            <div className="key-innovations">
              <h3>Key innovations in the Aledger economic model include:</h3>
              <ul>
                <li>
                  <strong>Hardware-Enforced Staking:</strong> Physical devices require significant token staking, 
                  creating natural supply constraints and value appreciation
                </li>
                <li>
                  <strong>Dual-Currency System:</strong> Separation of mining rewards (points) from governance 
                  and staking (AFI tokens)
                </li>
                <li>
                  <strong>Node-Controlled Synthesis:</strong> After initial Genesis period, package creation rights 
                  transfer exclusively to top-tier nodes
                </li>
                <li>
                  <strong>Time-Locked Conversion:</strong> Points-to-token conversion requires waiting periods 
                  with penalties for early withdrawal
                </li>
                <li>
                  <strong>Value Arbitrage Framework:</strong> Creates natural economic incentives for points trade, 
                  synthesis growth, and ecosystem expansion
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="hardware-staking-section">
        <div className="container">
          <div className="section-header">
            <h2>Hardware-Enforced Staking Model</h2>
            <p>The mathematical engine behind Aledger's explosive growth potential</p>
          </div>

          <div className="glass-card">
            <h3>Unprecedented Supply-Demand Mechanism</h3>
            <p>
              Aledger introduces a revolutionary economic model through hardware-enforced staking, which creates 
              natural token demand proportional to network growth. This mechanism establishes mathematically inevitable 
              supply constraints as the network expands.
            </p>
            
            <div className="staking-requirements">
              <h4>Staking Requirements Per Device:</h4>
              <ul>
                <li>Each device requires minimum AFI staking for activation</li>
                <li>Optimal returns require full capacity staking</li>
                <li>Staking is locked for the duration of the selected period</li>
              </ul>
              <p>
                This creates direct correlation between network growth and token demand, where each new device 
                requires a significant quantity of tokens to be removed from circulation.
              </p>
            </div>

            <div className="token-lockup-table">
              <h4>Token Lock-up Projections</h4>
              <div className="table-container">
                <table>
                  <thead>
                    <tr>
                      <th>Device Count</th>
                      <th>Total Staking Demand</th>
                      <th>vs. Total Supply</th>
                      <th>Supply Impact</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>5,000</td>
                      <td>300M AFI</td>
                      <td>30% locked</td>
                      <td>Significant supply reduction</td>
                    </tr>
                    <tr>
                      <td>10,000</td>
                      <td>600M AFI</td>
                      <td>60% locked</td>
                      <td>Major supply constraint</td>
                    </tr>
                    <tr>
                      <td>25,000</td>
                      <td>1.5B AFI</td>
                      <td>150% (demand > supply)</td>
                      <td>Supply-demand imbalance</td>
                    </tr>
                    <tr>
                      <td>50,000</td>
                      <td>3B AFI</td>
                      <td>300% (demand >> supply)</td>
                      <td>Extreme supply shortage</td>
                    </tr>
                    <tr>
                      <td>100,000</td>
                      <td>6B AFI</td>
                      <td>600% (extreme imbalance)</td>
                      <td>Mathematical scarcity</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <p className="table-caption">
                When demand for staking exceeds the total token supply, price appreciation becomes the only mechanism to resolve the supply-demand imbalance.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="growth-projections">
        <div className="container">
          <div className="section-header">
            <h2>Growth Projections</h2>
            <p>From initial launch to global infrastructure</p>
          </div>

          <div className="phases-grid">
            <div className="phase-card glass-card">
              <div className="phase-header">
                <span className="phase-tag">Phase 1</span>
                <h3>Initial 5,000 Packages</h3>
                <span className="phase-timeline">Months 1-6</span>
              </div>
              <ul>
                <li>Genesis packages sell through direct purchase</li>
                <li>Approximately 30% of total token supply locked in staking</li>
                <li>Initial community and node structure established</li>
              </ul>
            </div>

            <div className="phase-card glass-card">
              <div className="phase-header">
                <span className="phase-tag">Phase 2</span>
                <h3>Expansion to 50,000 Packages</h3>
                <span className="phase-timeline">Months 6-12</span>
              </div>
              <ul>
                <li>Shift to node-controlled synthesis model</li>
                <li>Staking demand exceeds total supply (300% imbalance)</li>
                <li>Secondary marketplace becomes primary distribution channel</li>
              </ul>
            </div>

            <div className="phase-card glass-card">
              <div className="phase-header">
                <span className="phase-tag">Phase 3</span>
                <h3>Path to 1 Million Devices</h3>
                <span className="phase-timeline">Years 1-3</span>
              </div>
              <ul>
                <li>Mature ecosystem with self-sustaining economics</li>
                <li>Extreme staking demand vs. supply imbalance</li>
                <li>Annual renewal revenue creates stability</li>
              </ul>
            </div>
          </div>

          <div className="price-projection glass-card">
            <h3>Token Price Projections Based on Supply-Demand Mathematics</h3>
            <div className="price-formula">
              <p><strong>Token Price = (Total Staking Demand + Node Staking Demand) ÷ Available Supply × Initial Price × Market Coefficient</strong></p>
            </div>
            <div className="table-container">
              <table>
                <thead>
                  <tr>
                    <th>Device Count</th>
                    <th>Total Staking Demand</th>
                    <th>vs. Total Supply</th>
                    <th>Estimated Price Range</th>
                    <th>Timeline</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>5,000</td>
                    <td>300M AFI</td>
                    <td>30% locked</td>
                    <td>$0.10-$0.30</td>
                    <td>1 month</td>
                  </tr>
                  <tr>
                    <td>10,000</td>
                    <td>600M AFI</td>
                    <td>60% locked</td>
                    <td>$0.30-$0.80</td>
                    <td>2 months</td>
                  </tr>
                  <tr>
                    <td>25,000</td>
                    <td>1.5B AFI</td>
                    <td>150%</td>
                    <td>$0.80-$2.00</td>
                    <td>3-4 months</td>
                  </tr>
                  <tr>
                    <td>50,000</td>
                    <td>3B AFI</td>
                    <td>300%</td>
                    <td>$2.00-$5.00</td>
                    <td>6 months</td>
                  </tr>
                  <tr>
                    <td>100,000</td>
                    <td>6B AFI</td>
                    <td>600%</td>
                    <td>$5.00-$15.00</td>
                    <td>12 months</td>
                  </tr>
                  <tr>
                    <td>500,000</td>
                    <td>30B AFI</td>
                    <td>3000%</td>
                    <td>$20.00-$50.00</td>
                    <td>24-36 months</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="disclaimer">
              This growth is not speculative prediction but the mathematical result of hardware staking mechanics. When device count 
              grows beyond what the total supply can support, price appreciation is the only market adjustment mechanism.
            </p>
          </div>
        </div>
      </section>

      <section className="sustainable-revenue">
        <div className="container">
          <div className="section-header">
            <h2>Sustainable Revenue Model</h2>
            <p>Long-term economic stability beyond initial growth</p>
          </div>
          
          <div className="glass-card">
            <div className="revenue-streams">
              <h3>Multiple Protocol Revenue Streams</h3>
              <div className="streams-grid">
                <div className="stream-item">
                  <h4>Genesis Sales</h4>
                  <p>5,000 packages at $499-$4,999 = $5-15 million initial revenue</p>
                </div>
                <div className="stream-item">
                  <h4>Registration Fees</h4>
                  <p>200-500 AFI per device registration</p>
                  <p>50% burned, 50% to node reward pool</p>
                </div>
                <div className="stream-item">
                  <h4>Marketplace Fees</h4>
                  <p>10% fee on all secondary market package sales</p>
                  <p>2.5% fee on other marketplace transactions</p>
                </div>
                <div className="stream-item">
                  <h4>Annual Renewal Fees</h4>
                  <p>$400 per device annual renewal</p>
                  <p>Projected $40M annual revenue at 100,000 devices</p>
                  <p>50% for development, 50% for token buyback and burn</p>
                </div>
              </div>
            </div>

            <div className="renewal-impact">
              <h3>Annual Renewal Fee Impact</h3>
              <div className="table-container">
                <table>
                  <thead>
                    <tr>
                      <th>Device Count</th>
                      <th>Annual Renewal Revenue</th>
                      <th>Buyback Amount (50%)</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>50,000</td>
                      <td>$20,000,000</td>
                      <td>$10,000,000</td>
                    </tr>
                    <tr>
                      <td>100,000</td>
                      <td>$40,000,000</td>
                      <td>$20,000,000</td>
                    </tr>
                    <tr>
                      <td>500,000</td>
                      <td>$200,000,000</td>
                      <td>$100,000,000</td>
                    </tr>
                    <tr>
                      <td>1,000,000</td>
                      <td>$400,000,000</td>
                      <td>$200,000,000</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <p>
                When network reaches 100,000 devices, annual buyback funds of $20,000,000 would remove 4,000,000 AFI 
                from circulation at $5 per token, creating continuous deflationary pressure.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="cta-section">
        <div className="container">
          <h2>Experience The Future of Tokenomics</h2>
          <p>Join the Aledger ecosystem and be part of a revolutionary economic model that transforms how value is created and distributed.</p>
          <div className="cta-buttons">
            <Link to="/team-leaders" className="btn btn-primary">Become a Team Leader</Link>
            <Link to="/node" className="btn btn-outline">Explore Node Opportunities</Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default WhitepaperPage;
